import { useEffect, useRef, useState } from "react";
import AcceleratorDoc, {
  AcceleratorDocStoreKey,
} from "../../models/AcceleratorDoc";
import DocTypeConfig from "../../models/config/DocTypeConfig";
import { HighlightColours } from "../../models/HighlightColors";
import { Hydratable } from "../../models/hydration/Hydratable";
import { Matcher } from "../../models/matchers/Matcher";
import { RegexMatcher } from "../../models/matchers/RegexMatcher";
import Model, { ModelStoreKey } from "../../models/Model";
import "../../styles/buttons.css";
import "../../styles/circles.css";
import "../../styles/documentClassification.css";
import "../../styles/loading.css";
import { Button } from "../buttons/Button";
import { VisibilityToggle } from "../buttons/VisibilityToggle";
import { HydratableDocError } from "../errorScreens/HydratableDocError";
import { HydratableModelError } from "../errorScreens/HydratableModelError";
import { useClassificationRuleHighlights } from "../hooks/useClassificationRuleHighlights";
import Icon from "../Icon";
import { MatcherEditor } from "../matcherEditing/MatcherEditor";
import { ModelNameChanger } from "../ModelNameChanger";
import { SizeHeader } from "../SizeHeader";
import { HighlightedSpans } from "../SpansHighlightable";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import {
  TTDocumentEndPatterns,
  TTDocumentStartPatterns,
  TTViewOcrEnd,
  TTViewOcrStart,
} from "../tooltips/ConfigurationTooltips";
import { CantSaveWarning } from "./CantSaveWarning";
import { GeneralModelAlert } from "./GeneralModelAlert";
import { ModelTypeEnabledDocType } from "./ModelTypeEnabledDocType";
import { MultipleModelAlert } from "./MultipleModelAlert";

export { DocumentClassification };

type Props = {
  docTypes: DocTypeConfig[];
  acceleratorDoc:
    | Hydratable<AcceleratorDoc, AcceleratorDocStoreKey>
    | undefined;
  model: Hydratable<Model, ModelStoreKey> | undefined;
  modelChanged: (modified: Model) => void;
  modelNameChanged: (newName: string, modified: Model) => void;
  documentStale: () => void;
  activeStartRuleIndex: number | null;
  setActiveStartRuleIndex: (index: number | null) => void;
};

const DocumentClassification = (props: Props) => {
  const [firstPageBox, setFirstPageBox] = useState<boolean>(false);
  const [allSpansBox, setAllSpansBox] = useState<boolean>(false);
  const { activeStartRuleIndex, setActiveStartRuleIndex } = props;

  const acceleratorDoc = props.acceleratorDoc?.isHydrated()
    ? props.acceleratorDoc.getHydrated()
    : undefined;
  const model = props.model?.isHydrated()
    ? props.model.getHydrated()
    : undefined;

  const [
    startRuleHighlights,
    triggerStartRuleHighlights,
    loadingStartRules,
    dispatchStartRule,
  ] = useClassificationRuleHighlights(
    props.acceleratorDoc,
    model?.documentStartRules ?? [],
    props.documentStale,
  );

  const [
    endRuleHighlights,
    triggerEndRuleHighlights,
    loadingEndRules,
    dispatchEndRule,
  ] = useClassificationRuleHighlights(
    props.acceleratorDoc,
    model?.documentEndRules ?? [],
    props.documentStale,
  );

  // if no hydratable provided, no doc selected
  if (!acceleratorDoc) {
    return <HydratableDocError acceleratorDoc={props.acceleratorDoc} />;
  }

  if (!model) {
    return <HydratableModelError model={props.model} />;
  }

  const getPageSpansById = useRef(
    acceleratorDoc.getPageSpansById.bind(acceleratorDoc),
  ).current!;
  const pageIds = useRef(
    acceleratorDoc.pages.map((x) => x.externalPageIdentifier),
  ).current!;

  useEffect(() => {
    const handleGlobalMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const clickedInImageViewer =
        !!target.closest(".imageViewer") ||
        !!target.closest(".canvas-container") ||
        target.classList.contains("upper-canvas");

      const clickedInAnyEditor =
        !!target.closest(".locationMatcherEditor") ||
        !!target.closest(".regexMatcherEditor") ||
        !!target.closest(".fuzzyMatcherEditor");

      if (!clickedInImageViewer && !clickedInAnyEditor) {
        setActiveStartRuleIndex(null);
      }
    };

    document.addEventListener("mousedown", handleGlobalMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleGlobalMouseDown);
    };
  }, [setActiveStartRuleIndex]);

  useEffect(() => {
    triggerStartRuleHighlights();
    triggerEndRuleHighlights();
  }, []);

  useEffect(() => {
    triggerStartRuleHighlights();
    triggerEndRuleHighlights();
  }, [props.model]);

  const configuredStartRules = model.documentStartRules.filter((x) =>
    x.isConfigured(),
  ).length;
  const configuredEndRules = model.documentEndRules.filter((x) =>
    x.isConfigured(),
  ).length;
  // get start rules with at least 1 extract span and matched on the first page index of the document
  const matchedStartRules = startRuleHighlights.filter(
    (x) => x[1].length !== 0 && x[2] === 0,
  ).length;
  const matchedEndRules = endRuleHighlights.filter(
    (x) => x[1].length !== 0,
  ).length;

  const selectedStartSpans =
    activeStartRuleIndex !== null
      ? [startRuleHighlights[activeStartRuleIndex]?.[1] ?? []]
      : startRuleHighlights.map((x) => x[1]);

  const startGroupColors =
    activeStartRuleIndex !== null
      ? [HighlightColours.getHighlightColor(activeStartRuleIndex)]
      : undefined;

  return (
    <>
      <div className={"documentClassification"}>
        {(acceleratorDoc.classifiedModel.templateType === "GENERAL" ||
          acceleratorDoc.classifiedModel.templateType === "FORMAT") && (
          <GeneralModelAlert
            batchClassIdentifier={acceleratorDoc.getBatchClassId()}
            classifiedModelName={acceleratorDoc.classifiedModel.name}
            templateType={acceleratorDoc.classifiedModel.templateType}
          />
        )}
        {acceleratorDoc.statuses.indexOf("MULTIPLE") > -1 && (
          <MultipleModelAlert docUuid={acceleratorDoc.uuid} />
        )}

        <ModelNameChanger
          batchClassId={model.storeId.batchClassIdentifier}
          modelName={model.storeId.name}
          changeModelName={(newName: string) => {
            props.modelNameChanged(newName, model);
          }}
        />

        <div className={"row typeRow"}>
          <ModelTypeEnabledDocType
            {...model}
            docTypes={props.docTypes}
            onDocType={(dtc) => {
              model.changeDocType(dtc);
              props.modelChanged(model);
            }}
            onForceValidate={(fv) => {
              model.forceValidate = fv;
              props.modelChanged(model);
            }}
            onEnabled={(enabled) => {
              model.enabled = enabled;
              props.modelChanged(model);
            }}
            onTemplateType={(type) => {
              model.setTemplateType(type);
              props.modelChanged(model);
            }}
            onStatus={(status) => {
              model.setStatus(status);
              props.modelChanged(model);
            }}
            onInheritTransformRules={(inheritTransformRules) => {
              console.log(
                "setting inheritTransformRules",
                inheritTransformRules,
              );
              model.inheritTransformRules = inheritTransformRules;
              console.log(model);
              props.modelChanged(model);
            }}
          />

          <CantSaveWarning
            docType={model.docType}
            modelName={model.storeId.name}
            startRuleCount={model.documentStartRules.length}
          />
        </div>
        <AdvancedTooltip tooltipText={TTDocumentStartPatterns}>
          {(tProps) => (
            <SizeHeader
              className={"row"}
              array={model.documentStartRules}
              tProps={tProps}
            >
              Document Start Pattern
            </SizeHeader>
          )}
        </AdvancedTooltip>
        {/* Add Rule / Rule Type / Show OCR */}
        <div className={"row"}>
          <AdvancedTooltip tooltipText={TTDocumentStartPatterns}>
            {(tProps) => (
              <Button
                {...tProps}
                style={{ marginBottom: 5 }}
                onClick={() => {
                  setFirstPageBox(true);
                  const docTypeConfig = props.docTypes.find(
                    (x) => x.typeName === model.docType,
                  )!;
                  model.addStartRule(
                    RegexMatcher.createTest({
                      threshold: docTypeConfig.getDefaultMatcherThresold(),
                    }),
                  );
                  props.modelChanged(model);
                }}
              >
                Add Rule
              </Button>
            )}
          </AdvancedTooltip>

          {model.documentStartRules.length > 0 && (
            <AdvancedTooltip overflow tooltipText={TTViewOcrStart}>
              {(tProps) => (
                <VisibilityToggle
                  tProps={tProps}
                  active={firstPageBox}
                  onClick={() => setFirstPageBox(!firstPageBox)}
                >
                  View OCR
                </VisibilityToggle>
              )}
            </AdvancedTooltip>
          )}
        </div>
        {/* Not All Start Rules Classified error */}
        <div className={"row"}>
          {!loadingStartRules &&
            matchedStartRules !== model.documentStartRules.length && (
              <span className={"error"}>
                {matchedStartRules}/{model.documentStartRules.length} Start
                Rules Matched For The First Page
              </span>
            )}
        </div>
        {/* Matcher Editors */}
        {model.documentStartRules.map((value: Matcher, index: number) => (
          <div
            key={`${value.getMatcherId()}-${value.position}`}
            className={"row"}
            onClick={() => setActiveStartRuleIndex(index)}
            onFocusCapture={() => setActiveStartRuleIndex(index)}
          >
            <MatcherEditor
              index={index}
              matcher={value}
              acceleratorDoc={props.acceleratorDoc}
              pageIndex={0}
              active={activeStartRuleIndex === index}
              setActive={(active) =>
                setActiveStartRuleIndex(active ? index : null)
              }
              defaultMatcherThreshold={
                props.docTypes
                  .find((x) => x.typeName === model.docType)
                  ?.getDefaultMatcherThresold() ?? 50
              }
              changedMatcherType={(matcher, oldMatcherId) => {
                dispatchStartRule({
                  type: "remove",
                  data: [[oldMatcherId, null, 0]],
                });
                model.replaceStartRule(matcher, index);
                setActiveStartRuleIndex(index);
                triggerStartRuleHighlights();
                props.modelChanged(model);
              }}
              labelColor={
                startRuleHighlights.some(
                  (x) =>
                    x[0] === value.getMatcherId() &&
                    x[1].length !== 0 &&
                    x[2] === 0,
                )
                  ? "black"
                  : "red"
              }
              labelBgColor={HighlightColours.getHighlightColor(index)}
              update={(matcher, oldMatcherId) => {
                dispatchStartRule({
                  type: "remove",
                  data: [[oldMatcherId, null, 0]],
                });
                // remove old value matcher id
                triggerStartRuleHighlights();
                props.modelChanged(model);
              }}
              button={
                <Button
                  onClick={() => {
                    dispatchStartRule({
                      type: "remove",
                      data: [[value.getMatcherId(), null, 0]],
                    });
                    model.removeStartRule(value);
                    setFirstPageBox(
                      firstPageBox && model.documentStartRules.length > 0,
                    );
                    props.modelChanged(model);
                  }}
                >
                  <Icon altText={"Delete Start Rule"} icon={"trash"} />
                </Button>
              }
            />
          </div>
        ))}

        {model.documentStartRules.length > 0 && firstPageBox && (
          <HighlightedSpans
            pageIds={pageIds}
            getPageSpansById={getPageSpansById}
            selectedSpans={selectedStartSpans}
            groupColors={startGroupColors}
          />
        )}
        <AdvancedTooltip tooltipText={TTDocumentEndPatterns}>
          {(tProps) => (
            <SizeHeader
              className={"row endRules"}
              array={model.documentEndRules}
              tProps={tProps}
            >
              Document End Patterns
            </SizeHeader>
          )}
        </AdvancedTooltip>
        {/* Add Rule / Rule Type / Show OCR */}
        <div className={"row"}>
          <AdvancedTooltip tooltipText={TTDocumentEndPatterns}>
            {(tProps) => (
              <Button
                {...tProps}
                style={{ marginBottom: 5 }}
                onClick={() => {
                  setAllSpansBox(true);
                  const docTypeConfig = props.docTypes.find(
                    (x) => x.typeName === model.docType,
                  )!;
                  model.addEndRule(
                    RegexMatcher.createTest({
                      threshold: docTypeConfig.getDefaultMatcherThresold(),
                    }),
                  );
                  props.modelChanged(model);
                }}
              >
                Add Rule
              </Button>
            )}
          </AdvancedTooltip>
          {model.documentEndRules.length > 0 && (
            <AdvancedTooltip overflow tooltipText={TTViewOcrEnd}>
              {(tProps) => (
                <VisibilityToggle
                  tProps={tProps}
                  active={allSpansBox}
                  onClick={() => setAllSpansBox(!allSpansBox)}
                >
                  View OCR
                </VisibilityToggle>
              )}
            </AdvancedTooltip>
          )}
        </div>
        {/* Not All End Rules Classified error */}
        <div className={"row"}>
          {!loadingEndRules &&
            matchedEndRules !== model.documentEndRules.length && (
              <span className={"error"}>
                {matchedEndRules}/{model.documentEndRules.length} End Rules
                Matched For This Document
              </span>
            )}
        </div>
        {/* Matcher Editors */}
        {model.documentEndRules.map((value: Matcher, index: number) => (
          <div
            key={`${value.getMatcherId()}-${value.position}`}
            className={"row"}
          >
            <MatcherEditor
              index={index}
              matcher={value}
              defaultMatcherThreshold={
                props.docTypes
                  .find((x) => x.typeName === model.docType)
                  ?.getDefaultMatcherThresold() ?? 50
              }
              labelColor={"black"}
              labelBgColor={HighlightColours.getHighlightColor(index)}
              changedMatcherType={(matcher, oldMatcherId) => {
                dispatchEndRule({
                  type: "remove",
                  data: [[oldMatcherId, null, 0]],
                });
                model.replaceEndRule(matcher, index);
                triggerEndRuleHighlights();
                props.modelChanged(model);
              }}
              update={(matcher, oldMatcherId) => {
                dispatchEndRule({
                  type: "remove",
                  data: [[oldMatcherId, null, 0]],
                });
                // remove old value matcher id
                triggerEndRuleHighlights();
                props.modelChanged(model);
              }}
              button={
                <Button
                  onClick={() => {
                    dispatchEndRule({
                      type: "remove",
                      data: [[value.getMatcherId(), null, 0]],
                    });
                    model.removeEndRule(value);
                    setAllSpansBox(
                      allSpansBox && model.documentEndRules.length > 0,
                    );
                    props.modelChanged(model);
                  }}
                >
                  <Icon altText={"Delete End Rule"} icon={"trash"} />
                </Button>
              }
            />
          </div>
        ))}
        {model.documentEndRules.length > 0 && allSpansBox && (
          <HighlightedSpans
            pageIds={pageIds}
            getPageSpansById={getPageSpansById}
            selectedSpans={endRuleHighlights.map((x) => x[1])}
            groupColors={endRuleHighlights
              .map((x) =>
                model.documentEndRules.findIndex(
                  (matcher) => matcher.getMatcherId() === x[0],
                ),
              )
              .map((x) => HighlightColours.getHighlightColor(x == -1 ? 0 : x))}
          />
        )}
      </div>
    </>
  );
};
